/**
 * Fractional indexing for stable, sparse ordering of kanban cards.
 *
 * Keys are strings using chars '0'-'9', 'a'-'z' (base-36, 36 chars).
 * They sort correctly using standard JavaScript string comparison.
 *
 * Key properties:
 *  - generateKeyBetween(lo, hi): returns a string strictly between lo and hi.
 *  - generateInitialKeys(n): returns n evenly-distributed initial keys.
 *  - Inserting between two adjacent keys always succeeds by appending chars.
 *  - Never need to rebalance; space is theoretically unlimited.
 */

const ALPHABET = '0123456789abcdefghijklmnopqrstuvwxyz';
const N = ALPHABET.length; // 36

function charVal(c) {
  const v = ALPHABET.indexOf(c);
  if (v < 0) throw new Error(`Invalid rank character: '${c}'`);
  return v;
}

function valChar(v) {
  return ALPHABET[v];
}

/**
 * Find a string strictly between lo and hi.
 * lo = '' means "before everything"; hi = '' means "after everything".
 */
function midStr(lo, hi) {
  let result = '';
  let i = 0;

  while (true) {
    const loVal = i < lo.length ? charVal(lo[i]) : 0;
    const hiVal = i < hi.length ? charVal(hi[i]) : N; // N = one past last char
    const gap = hiVal - loVal;

    if (gap >= 2) {
      result += valChar(Math.floor((loVal + hiVal) / 2));
      return result;
    }

    if (gap === 1) {
      // Adjacent: take lo's character, then find something after lo's remainder
      result += valChar(loVal);
      return result + midStr(lo.slice(i + 1), '');
    }

    // gap === 0: same character, advance to next position
    result += valChar(loVal);
    i++;

    if (i > lo.length + 64) {
      // Safety guard — should never happen if lo < hi is guaranteed
      throw new Error('generateKeyBetween: rank space exhausted (too many insertions at same position)');
    }
  }
}

/**
 * Generate a rank key strictly between lo and hi.
 *
 * @param {string|null} lo  Lower bound (null = beginning of space).
 * @param {string|null} hi  Upper bound (null = end of space).
 * @returns {string}
 */
export function generateKeyBetween(lo, hi) {
  const a = lo ?? '';
  const b = hi ?? '';

  if (a !== '' && b !== '' && a >= b) {
    throw new Error(`generateKeyBetween: lo ('${a}') must be less than hi ('${b}')`);
  }

  return midStr(a, b);
}

/**
 * Generate n evenly-distributed initial rank keys.
 * Keys are single characters in the 'a'-'z' range (comfortable middle of space).
 * For n > 26, uses two-character keys.
 *
 * @param {number} n
 * @returns {string[]}
 */
export function generateInitialKeys(n) {
  if (n <= 0) return [];

  const keys = [];
  // 'a' is at index 10, 'z' at index 35 — use this range for initial keys
  const rangeStart = 10;
  const rangeEnd = 36;
  const rangeSize = rangeEnd - rangeStart; // 26

  if (n <= rangeSize) {
    for (let i = 0; i < n; i++) {
      const pos = rangeStart + Math.floor((i + 0.5) * rangeSize / n);
      keys.push(valChar(pos));
    }
  } else {
    // Two-character keys: 'a0' through 'z9'...'za'...'zz'
    const total = rangeSize * N;
    for (let i = 0; i < n; i++) {
      const pos = Math.floor((i + 0.5) * total / n);
      const first = rangeStart + Math.floor(pos / N);
      const second = pos % N;
      keys.push(valChar(Math.min(first, 35)) + valChar(second));
    }
  }

  return keys;
}

/**
 * Sort comparator for tasks by rank, nulls last.
 * Usage: tasks.sort(compareByRank)
 */
export function compareByRank(a, b) {
  const ra = a?.data?.rank ?? null;
  const rb = b?.data?.rank ?? null;
  if (ra === rb) return 0;
  if (ra === null || ra === '') return 1;
  if (rb === null || rb === '') return -1;
  return ra < rb ? -1 : 1;
}

/**
 * Sort tasks within each column by rank, preserving inter-column order.
 * Tasks without a rank go to the end of their column.
 *
 * @param {Array} tasks  Flat tasks array (each task has data.columnId, data.rank).
 * @returns {Array}      New array sorted by (columnId group, rank).
 */
export function sortTasksByRankWithinColumns(tasks) {
  // Preserve the order in which columns first appear
  const columnOrder = [];
  const byColumn = {};

  for (const task of tasks) {
    const col = task.data.columnId;
    if (!byColumn[col]) {
      byColumn[col] = [];
      columnOrder.push(col);
    }
    byColumn[col].push(task);
  }

  for (const col of columnOrder) {
    byColumn[col].sort(compareByRank);
  }

  return columnOrder.flatMap(col => byColumn[col]);
}
